import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight, BookOpenText, CalendarDays, Clock, FileText, MapPin, Newspaper, Search } from 'lucide-react';
import Meta from '../../components/Meta';
import { posts, type BlogPost } from '../../lib/blog/posts';
import { fetchGeneratedPlanningBlogPosts } from '../../lib/planningArticles';
import { SITE_URL } from '../../lib/siteUrl';
import { formatBlogDate } from '../../lib/blog/date';

type SectionKind = 'planning' | 'editorial';

function PostMeta({ post }: { post: BlogPost }) {
  return (
    <div className="pr-data flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px]">
      <span className="inline-flex items-center gap-1.5">
        <CalendarDays className="h-3 w-3" />
        {formatBlogDate(post.publishedAt)}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Clock className="h-3 w-3" />
        {post.readingTime} min read
      </span>
      <span className="inline-flex items-center gap-1.5">
        <MapPin className="h-3 w-3" />
        {post.location}
      </span>
    </div>
  );
}

function PostCard({
  post,
  index,
  kind,
  featured = false,
}: {
  post: BlogPost;
  index: number;
  kind: SectionKind;
  featured?: boolean;
}) {
  const Icon = kind === 'planning' ? FileText : BookOpenText;
  const label = kind === 'planning' ? 'Planning Watch' : 'Editorial';

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.38, delay: Math.min(index * 0.045, 0.22) }}
      className="h-full"
    >
      <Link href={`/blog/${post.slug}`} scroll className="group block h-full">
        <div
          className={`pr-panel relative flex h-full overflow-hidden transition-colors hover:border-[var(--c-text-tertiary)] hover:bg-[var(--c-surface-hover)] ${featured ? 'min-h-[320px] flex-col justify-between border-t-2 border-t-[var(--c-text-primary)] p-6 sm:p-8 lg:p-9' : 'flex-col p-5'}`}
        >
          <div className="flex items-center justify-between gap-4">
            <span className="pr-section-label inline-flex items-center gap-2">
              <Icon className="h-3.5 w-3.5" />
              {label}
            </span>
            {post.coverLabel ? (
              <span className="pr-data max-w-[45%] truncate border border-[var(--c-border)] bg-[var(--c-bg-secondary)] px-2.5 py-1 text-[10px] uppercase tracking-[0.14em]">
                {post.coverLabel}
              </span>
            ) : null}
          </div>

          <div className={featured ? 'mt-10 max-w-3xl' : 'mt-6'}>
            <h3
              className={`font-['Newsreader'] font-medium leading-tight text-[var(--c-text-primary)] transition-colors group-hover:text-[var(--c-accent)] ${featured ? 'text-3xl sm:text-4xl lg:text-[2.75rem]' : 'text-2xl'}`}
            >
              {post.title}
            </h3>
            <p className={`mt-4 leading-relaxed text-[var(--c-text-secondary)] ${featured ? 'max-w-2xl text-base sm:text-lg' : 'text-sm'}`}>
              {post.excerpt}
            </p>
          </div>

          <div className={featured ? 'mt-10 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between' : 'mt-7 flex flex-1 flex-col justify-end gap-5'}>
            <PostMeta post={post} />
            <span className="pr-textlink">
              Read
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}

function SectionHeader({
  id,
  icon,
  title,
  description,
  count,
}: {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  count: number;
}) {
  return (
    <div className="mb-7 flex flex-col gap-5 border-t border-[var(--c-border)] pt-8 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-2xl">
        <div className="pr-section-label mb-3 inline-flex items-center gap-2">
          {icon}
          {count} {count === 1 ? 'article' : 'articles'}
        </div>
        <h2 id={id} className="pr-heading text-3xl sm:text-4xl">
          {title}
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-[var(--c-text-secondary)] sm:text-base">
          {description}
        </p>
      </div>
    </div>
  );
}

function EmptyPlanningState() {
  return (
    <div className="pr-panel border-dashed p-6">
      <div className="flex items-start gap-4">
        <Search className="mt-1 h-5 w-5 shrink-0 text-[var(--c-accent)]" />
        <div>
          <h3 className="font-['Newsreader'] text-2xl font-medium text-[var(--c-text-primary)]">
            Planning articles will appear here
          </h3>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--c-text-secondary)]">
            Worth-watching planning application articles are published from the backend article layer and kept separate from manual editorial posts.
          </p>
        </div>
      </div>
    </div>
  );
}

function buildJournalStructuredData(generatedPosts: BlogPost[], manualPosts: BlogPost[]) {
  const allPosts = [...generatedPosts, ...manualPosts].sort(
    (left, right) => new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime()
  );

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Blog',
        '@id': `${SITE_URL}/blog#journal`,
        name: 'City Scope Journal',
        url: `${SITE_URL}/blog`,
        description: 'Planning application analysis, worth-watching development stories and editorial notes from City Scope.',
        publisher: {
          '@type': 'Organization',
          '@id': `${SITE_URL}/#organization`,
          name: 'City Scope',
          url: SITE_URL,
        },
        blogPost: allPosts.slice(0, 100).map((post) => ({
          '@type': 'BlogPosting',
          headline: post.title,
          url: `${SITE_URL}/blog/${post.slug}`,
          datePublished: post.publishedAt,
          author: {
            '@type': 'Person',
            name: post.author,
          },
        })),
      },
      {
        '@type': 'CollectionPage',
        '@id': `${SITE_URL}/blog#webpage`,
        url: `${SITE_URL}/blog`,
        name: 'Journal | City Scope',
        description: 'Planning application analysis, worth-watching development stories and editorial notes from City Scope.',
        inLanguage: 'en-GB',
        isPartOf: {
          '@id': `${SITE_URL}/#website`,
        },
        mainEntity: {
          '@id': `${SITE_URL}/blog#journal`,
        },
      },
      {
        '@type': 'ItemList',
        '@id': `${SITE_URL}/blog#planning-articles`,
        name: 'Worth-watching planning applications',
        itemListElement: generatedPosts.map((post, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `${SITE_URL}/blog/${post.slug}`,
          name: post.title,
        })),
      },
      {
        '@type': 'ItemList',
        '@id': `${SITE_URL}/blog#manual-posts`,
        name: 'Manual Journal posts',
        itemListElement: manualPosts.map((post, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `${SITE_URL}/blog/${post.slug}`,
          name: post.title,
        })),
      },
    ],
  };
}

export default function BlogIndexPage({ generatedPosts = [] }: { generatedPosts?: BlogPost[] }) {
  const [latestPlanning, ...otherPlanningPosts] = [...generatedPosts].sort(
    (left, right) => new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime()
  );
  const manualPosts = [...posts].sort(
    (left, right) => new Date(right.publishedAt).getTime() - new Date(left.publishedAt).getTime()
  );
  const structuredData = buildJournalStructuredData(generatedPosts, manualPosts);

  return (
    <>
      <Meta
        title="Journal | City Scope"
        description="Planning application analysis, worth-watching development stories and editorial notes from City Scope."
        canonical="/blog"
        ogType="website"
        structuredData={structuredData}
      />

      <div className="planning-light">
        <main className="planning-register min-h-screen bg-[var(--c-bg)] text-[var(--c-text-primary)]">
        <header className="px-4 pt-[calc(var(--navbar-height,64px)+3rem)] sm:px-6 lg:px-8 lg:pt-[calc(var(--navbar-height,64px)+4rem)]">
          <div className="mx-auto max-w-7xl border-b border-[var(--c-border)] pb-12">
            <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
              <div className="flex min-h-[260px] flex-col justify-between">
                <div>
                  <p className="pr-kicker pr-kicker--accent">CityScope / Journal</p>
                  <h1 className="pr-masthead mt-5 max-w-4xl">
                    <span className="mr-6 inline-block">Planning</span><em>journal</em>
                  </h1>
                  <p className="mt-6 max-w-xl text-base leading-relaxed text-[var(--c-text-secondary)] sm:text-lg">
                    Planning application stories, development signals and editorial notes from City Scope.
                  </p>
                </div>
                <div className="mt-10 grid max-w-xl grid-cols-2 gap-3 sm:grid-cols-3">
                  <div className="border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-4">
                    <div className="pr-numeral text-2xl text-[var(--c-text-primary)]">
                      {generatedPosts.length}
                    </div>
                    <div className="pr-data mt-1 text-[10px] uppercase tracking-[0.16em]">
                      Planning
                    </div>
                  </div>
                  <div className="border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-4">
                    <div className="pr-numeral text-2xl text-[var(--c-text-primary)]">
                      {manualPosts.length}
                    </div>
                    <div className="pr-data mt-1 text-[10px] uppercase tracking-[0.16em]">
                      Editorial
                    </div>
                  </div>
                  <div className="col-span-2 border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-4 sm:col-span-1">
                    <div className="pr-numeral text-2xl text-[var(--c-text-primary)]">
                      Live
                    </div>
                    <div className="pr-data mt-1 text-[10px] uppercase tracking-[0.16em]">
                      Backend feed
                    </div>
                  </div>
                </div>
              </div>

              <div className="pr-panel border-t-2 border-t-[var(--c-text-primary)] p-5 sm:p-6">
                <div className="flex items-center justify-between gap-4 border-b border-[var(--c-border)] pb-4">
                  <span className="pr-section-label inline-flex items-center gap-2">
                    <Newspaper className="h-3.5 w-3.5" />
                    Latest planning story
                  </span>
                  <span className="pr-data text-[10px] uppercase tracking-[0.16em]">
                    Worth Watching
                  </span>
                </div>

                {latestPlanning ? (
                  <Link href={`/blog/${latestPlanning.slug}`} scroll className="group block pt-6">
                    <h2 className="font-['Newsreader'] text-3xl font-medium leading-tight text-[var(--c-text-primary)] transition-colors group-hover:text-[var(--c-accent)] sm:text-4xl">
                      {latestPlanning.title}
                    </h2>
                    <p className="mt-4 text-sm leading-relaxed text-[var(--c-text-secondary)] sm:text-base">
                      {latestPlanning.excerpt}
                    </p>
                    <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                      <PostMeta post={latestPlanning} />
                      <span className="pr-textlink">
                        Open article
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                ) : (
                  <div className="pt-6 text-sm leading-relaxed text-[var(--c-text-secondary)]">
                    No generated planning articles have been published yet.
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-7xl space-y-16 px-4 py-14 sm:px-6 lg:px-8 lg:py-18">
          <section aria-labelledby="planning-journal-heading">
            <SectionHeader
              id="planning-journal-heading"
              icon={<FileText className="h-3.5 w-3.5" />}
              title="Worth-watching planning applications"
              description="Generated articles for specific applications, kept apart from manual Journal posts so planning-watch content can scale without burying editorial notes."
              count={generatedPosts.length}
            />

            {latestPlanning ? (
              <div className="space-y-5">
                <div className={otherPlanningPosts.length > 0 ? 'grid gap-5 lg:grid-cols-[1.08fr_0.92fr]' : ''}>
                  <div className={otherPlanningPosts.length > 0 ? '' : 'max-w-4xl'}>
                    <PostCard post={latestPlanning} index={0} kind="planning" featured />
                  </div>
                  {otherPlanningPosts.length > 0 ? (
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
                      {otherPlanningPosts.slice(0, 2).map((post, index) => (
                        <PostCard key={post.slug} post={post} index={index + 1} kind="planning" />
                      ))}
                    </div>
                  ) : null}
                </div>
                {otherPlanningPosts.length > 2 ? (
                  <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    {otherPlanningPosts.slice(2).map((post, index) => (
                      <PostCard key={post.slug} post={post} index={index + 3} kind="planning" />
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <EmptyPlanningState />
            )}
          </section>

          <section aria-labelledby="editorial-journal-heading">
            <SectionHeader
              id="editorial-journal-heading"
              icon={<BookOpenText className="h-3.5 w-3.5" />}
              title="Manual Journal posts"
              description="Longer-form editorial pieces, product notes and analysis written separately from the application article generator."
              count={manualPosts.length}
            />

            {manualPosts.length > 0 ? (
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {manualPosts.map((post, index) => (
                  <PostCard key={post.slug} post={post} index={index} kind="editorial" />
                ))}
              </div>
            ) : (
              <p className="font-['DM_Mono'] text-sm text-[var(--c-text-secondary)]">
                Manual posts coming soon.
              </p>
            )}
          </section>
        </div>
        </main>
      </div>
    </>
  );
}

export async function getStaticProps() {
  const generatedPosts = await fetchGeneratedPlanningBlogPosts(100);
  return {
    props: {
      generatedPosts,
    },
    revalidate: 300,
  };
}
